import { useRef, useState } from "react";
import { Check, Image as ImageIcon, Loader2, Music, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { uploadChunkedFile } from "@/lib/upload";
import { GENRES } from "@/lib/genres";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

const AUDIO_EXTS = ["mp3", "wav", "m4a", "flac", "aif", "aiff"];
const COVER_EXTS = ["jpg", "jpeg", "png", "webp", "gif"];
const PARALLEL_UPLOADS = 3;

function parseFilename(name) {
  let base = name.replace(/\.[^.]+$/, "");
  const bpmMatch = base.match(/(\d{2,3})\s?bpm|bpm\s?(\d{2,3})/i);
  const bpm = bpmMatch ? parseInt(bpmMatch[1] || bpmMatch[2], 10) : 140;
  const keyMatch = base.match(/\b([A-G](?:#|b)?(?:m|min|maj|M)?)\b/);
  const musical_key = keyMatch ? keyMatch[1].replace(/min$/i, "m").replace(/maj$/i, "") : "";
  let cleaned = base
    .replace(/\[[^\]]*prod[^\]]*\]/gi, "")
    .replace(/\([^)]*prod[^)]*\)/gi, "")
    .replace(/prod(?:uced)?\.?\s*by\s*[\w. ]+/gi, "")
    .replace(/\d{2,3}\s?bpm/gi, "")
    .replace(/bpm\s?\d{2,3}/gi, "")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (keyMatch) {
    cleaned = cleaned.replace(new RegExp(`\\b${keyMatch[1]}\\b`), "").replace(/\s+/g, " ").trim();
  }
  const title = cleaned
    .split(" ")
    .filter(Boolean)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ") || base;
  return { title, bpm: isFinite(bpm) && bpm >= 40 && bpm <= 300 ? bpm : 140, musical_key };
}

export function BatchBeatUpload({ onDone }) {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState({
    genre: "Trap",
    price_mp3: 17.99,
    price_wav: 27.99,
    price_exclusive: 174.99,
    free_download: false,
  });
  const [dragActive, setDragActive] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);
  const rowsRef = useRef([]);
  rowsRef.current = rows;

  const addFiles = (files) => {
    const incoming = Array.from(files).filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return ext && AUDIO_EXTS.includes(ext);
    });
    if (incoming.length === 0) {
      toast.error("Drop MP3, WAV, FLAC or AIFF files");
      return;
    }
    setRows((current) => {
      const seen = new Set(current.map((r) => `${r.file.name}:${r.file.size}`));
      const fresh = incoming
        .filter((f) => !seen.has(`${f.name}:${f.size}`))
        .map((f, i) => {
          const parsed = parseFilename(f.name);
          return {
            id: `${Date.now()}-${current.length + i}-${f.name}`,
            file: f,
            title: parsed.title,
            genre: defaults.genre,
            bpm: parsed.bpm,
            musical_key: parsed.musical_key,
            tags: "",
            cover: null,
            coverPreview: "",
            status: "queued",
            progress: 0,
            error: "",
          };
        });
      return [...current, ...fresh];
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (running) return;
    addFiles(e.dataTransfer.files);
  };

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const attachCover = (id, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !COVER_EXTS.includes(ext)) {
      return toast.error("Cover must be JPG, PNG, WEBP or GIF");
    }
    if (file.size > 10 * 1024 * 1024) {
      return toast.error("Cover too large (max 10MB)");
    }
    // Revoke previous blob URL if any, to avoid leaks.
    const current = rowsRef.current.find((r) => r.id === id);
    if (current?.coverPreview?.startsWith("blob:")) {
      try { URL.revokeObjectURL(current.coverPreview); } catch { /* noop */ }
    }
    updateRow(id, { cover: file, coverPreview: URL.createObjectURL(file) });
  };

  const clearCover = (id) => {
    const current = rowsRef.current.find((r) => r.id === id);
    if (current?.coverPreview?.startsWith("blob:")) {
      try { URL.revokeObjectURL(current.coverPreview); } catch { /* noop */ }
    }
    updateRow(id, { cover: null, coverPreview: "" });
  };

  const removeRow = (id) => {
    clearCover(id);
    setRows((rs) => rs.filter((r) => r.id !== id));
  };

  const clearAll = () => {
    rowsRef.current.forEach((r) => {
      if (r.coverPreview?.startsWith("blob:")) {
        try { URL.revokeObjectURL(r.coverPreview); } catch { /* noop */ }
      }
    });
    setRows([]);
  };

  const applyGenreToAll = (genre) => {
    setDefaults((d) => ({ ...d, genre }));
    setRows((rs) => rs.map((r) => (r.status === "queued" ? { ...r, genre } : r)));
  };

  const processRow = async (row) => {
    try {
      updateRow(row.id, { status: "uploading", progress: 0, error: "" });
      const audioPath = await uploadChunkedFile(row.file, row.file.name, (msg) => {
        const pct = parseInt((msg.match(/(\d+)\s?%/) || [])[1] || "0", 10);
        updateRow(row.id, { progress: pct });
      });
      let coverPath = "";
      if (row.cover) {
        updateRow(row.id, { status: "uploading", progress: 100 });
        coverPath = await uploadChunkedFile(row.cover, `${row.file.name} cover`, () => {});
      }
      updateRow(row.id, { status: "saving", progress: 100 });
      const payload = {
        title: row.title.trim(),
        genre: row.genre,
        bpm: parseInt(row.bpm, 10) || 0,
        musical_key: row.musical_key || "",
        tags: row.tags || "",
        price_mp3: parseFloat(defaults.price_mp3) || 0,
        price_wav: parseFloat(defaults.price_wav) || 0,
        price_exclusive: parseFloat(defaults.price_exclusive) || 0,
        free_download: defaults.free_download,
        audio_path: audioPath,
        audio_filename: row.file.name,
        audio_content_type: row.file.type || "audio/mpeg",
        stem_paths: [],
        stem_filenames: [],
        cover_path: coverPath,
      };
      const { data: saved } = await api.post("/admin/beats/from-uploads", payload);
      updateRow(row.id, {
        status: "done",
        loudness_lufs: saved?.loudness_lufs ?? null,
        loudness_warning: !!saved?.loudness_warning,
      });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      updateRow(row.id, {
        status: "failed",
        error: typeof detail === "string" ? detail : "Upload failed",
      });
      return false;
    }
  };

  const uploadAll = async () => {
    const queue = rows.filter((r) => r.status === "queued" || r.status === "failed");
    if (queue.length === 0) return toast.info("Nothing to upload");
    const missing = queue.find((r) => !r.title.trim() || !r.bpm);
    if (missing) {
      updateRow(missing.id, { status: "failed", error: "Title & BPM required" });
      return toast.error(`Row "${missing.file.name}" is missing title or BPM`);
    }
    setRunning(true);
    let okCount = 0;
    // Worker pool: PARALLEL_UPLOADS beats race through the queue simultaneously.
    let cursor = 0;
    const worker = async () => {
      while (true) {
        const next = cursor;
        cursor += 1;
        const row = queue[next];
        if (!row) return;
        const ok = await processRow(row);
        if (ok) okCount += 1;
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL_UPLOADS, queue.length) }, () => worker()),
    );
    setRunning(false);
    if (okCount > 0) {
      toast.success(`${okCount} beat${okCount === 1 ? "" : "s"} uploaded`);
      onDone?.();
    }
    const failed = rowsRef.current.filter((r) => r.status === "failed").length;
    if (failed) toast.error(`${failed} failed — retry from the list`);
  };

  const clearDone = () => {
    rowsRef.current.forEach((r) => {
      if (r.status === "done" && r.coverPreview?.startsWith("blob:")) {
        try { URL.revokeObjectURL(r.coverPreview); } catch { /* noop */ }
      }
    });
    setRows((rs) => rs.filter((r) => r.status !== "done"));
  };

  const queuedCount = rows.filter((r) => r.status === "queued").length;
  const doneCount = rows.filter((r) => r.status === "done").length;

  return (
    <div className="space-y-4" data-testid="admin-batch-upload">
      <div
        onDragEnter={(e) => { e.preventDefault(); if (!running) setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); if (!running) setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => !running && inputRef.current?.click()}
        data-testid="admin-batch-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          dragActive ? "border-emerald-300 bg-emerald-300/10" : "border-white/15 bg-[#141419] hover:border-emerald-300/40"
        }`}
      >
        <Music size={28} className="mx-auto text-emerald-300" />
        <p className="mt-3 text-sm font-bold text-white">
          Drop MP3 / WAV / FLAC files here, or click to pick
        </p>
        <p className="mt-1 text-[11px] text-zinc-500">
          Multiple files land as separate beats. Titles, BPM and key are auto-parsed. Up to {PARALLEL_UPLOADS} uploads run at once.
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".mp3,.wav,.m4a,.flac,.aif,.aiff,audio/*"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="admin-batch-file-input"
        />
      </div>

      {rows.length > 0 && (
        <div
          className="grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-[#141419] p-4 sm:grid-cols-4"
          data-testid="admin-batch-defaults"
        >
          <label className="col-span-2 flex flex-col gap-1 sm:col-span-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Default genre</span>
            <select
              value={defaults.genre}
              onChange={(e) => applyGenreToAll(e.target.value)}
              className={inputCls}
              data-testid="admin-batch-default-genre"
            >
              {GENRES.map((g) => <option key={g}>{g}</option>)}
            </select>
          </label>
          {[
            ["price_mp3", "MP3 Lease $"],
            ["price_wav", "WAV+Stems $"],
            ["price_exclusive", "Exclusive $"],
          ].map(([k, label]) => (
            <label key={k} className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
              <input
                type="number"
                step="0.01"
                value={defaults[k]}
                onChange={(e) => setDefaults((d) => ({ ...d, [k]: e.target.value }))}
                className={inputCls}
                data-testid={`admin-batch-default-${k}`}
              />
            </label>
          ))}
          <label className="col-span-2 flex items-center gap-2 sm:col-span-4">
            <input
              type="checkbox"
              checked={defaults.free_download}
              onChange={(e) => setDefaults((d) => ({ ...d, free_download: e.target.checked }))}
              className="h-4 w-4 accent-emerald-300"
              data-testid="admin-batch-default-free"
            />
            <span className="text-xs text-zinc-400">Mark every beat as a free download</span>
          </label>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2" data-testid="admin-batch-rows">
          {rows.map((r) => {
            const locked = r.status === "done" || r.status === "uploading" || r.status === "saving";
            return (
            <div
              key={r.id}
              className={`rounded-2xl border p-3 ${
                r.status === "done"
                  ? "border-emerald-300/40 bg-emerald-300/5"
                  : r.status === "failed"
                    ? "border-red-500/40 bg-red-500/5"
                    : "border-white/10 bg-[#141419]"
              }`}
              data-testid={`admin-batch-row-${r.id}`}
            >
              <div className="flex items-start gap-3">
                <RowCoverSlot
                  row={r}
                  onPick={(f) => attachCover(r.id, f)}
                  onClear={() => clearCover(r.id)}
                  locked={locked}
                />
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="truncate font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {r.file.name} · {(r.file.size / (1024 * 1024)).toFixed(1)} MB
                    {r.cover && (
                      <span className="ml-2 rounded-full bg-sky-400/15 px-2 py-0.5 text-sky-300">cover ✓</span>
                    )}
                    {r.status === "done" && typeof r.loudness_lufs === "number" && (
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 ${
                          r.loudness_warning
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-emerald-300/15 text-emerald-200"
                        }`}
                        data-testid={`admin-batch-row-lufs-${r.id}`}
                      >
                        {r.loudness_warning ? "⚠ Hot " : ""}{r.loudness_lufs} LUFS
                      </span>
                    )}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    <input
                      className={`${inputCls} sm:col-span-2`}
                      value={r.title}
                      onChange={(e) => updateRow(r.id, { title: e.target.value })}
                      placeholder="Title"
                      disabled={locked}
                      data-testid={`admin-batch-row-title-${r.id}`}
                    />
                    <select
                      className={inputCls}
                      value={r.genre}
                      onChange={(e) => updateRow(r.id, { genre: e.target.value })}
                      disabled={locked}
                      data-testid={`admin-batch-row-genre-${r.id}`}
                    >
                      {GENRES.map((g) => <option key={g}>{g}</option>)}
                    </select>
                    <input
                      type="number"
                      className={inputCls}
                      value={r.bpm}
                      onChange={(e) => updateRow(r.id, { bpm: e.target.value })}
                      placeholder="BPM"
                      disabled={locked}
                      data-testid={`admin-batch-row-bpm-${r.id}`}
                    />
                    <input
                      className={inputCls}
                      value={r.musical_key}
                      onChange={(e) => updateRow(r.id, { musical_key: e.target.value })}
                      placeholder="Key"
                      disabled={locked}
                      data-testid={`admin-batch-row-key-${r.id}`}
                    />
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span
                    className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                      r.status === "done"
                        ? "bg-emerald-300/15 text-emerald-200"
                        : r.status === "failed"
                          ? "bg-red-500/15 text-red-300"
                          : r.status === "uploading" || r.status === "saving"
                            ? "bg-sky-400/15 text-sky-300"
                            : "bg-white/5 text-zinc-400"
                    }`}
                    data-testid={`admin-batch-row-status-${r.id}`}
                  >
                    {r.status === "uploading" ? `Uploading ${r.progress}%` : r.status}
                  </span>
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeRow(r.id)}
                      aria-label="Remove row"
                      data-testid={`admin-batch-row-remove-${r.id}`}
                      className="rounded-full border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              </div>
              {(r.status === "uploading" || r.status === "saving") && (
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full bg-emerald-300 transition-all"
                    style={{ width: `${r.status === "saving" ? 100 : r.progress}%` }}
                  />
                </div>
              )}
              {r.error && (
                <p className="mt-2 text-[11px] text-red-300" data-testid={`admin-batch-row-error-${r.id}`}>
                  {r.error}
                </p>
              )}
            </div>
          );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={uploadAll}
            disabled={running || queuedCount === 0}
            data-testid="admin-batch-upload-all"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50 sm:flex-none sm:px-6"
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Upload {queuedCount || rows.length} Beat{(queuedCount || rows.length) === 1 ? "" : "s"}
          </button>
          {doneCount > 0 && (
            <button
              type="button"
              onClick={clearDone}
              disabled={running}
              data-testid="admin-batch-clear-done"
              className="flex items-center gap-2 rounded-full border border-emerald-300/40 px-5 py-3 text-xs font-bold uppercase tracking-wider text-emerald-200 hover:bg-emerald-300/10 disabled:opacity-50"
            >
              <Check size={13} /> Clear {doneCount} Done
            </button>
          )}
          <button
            type="button"
            onClick={clearAll}
            disabled={running}
            data-testid="admin-batch-clear-all"
            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:border-red-500/40 hover:text-red-300 disabled:opacity-50"
          >
            <Trash2 size={13} /> Clear All
          </button>
        </div>
      )}
    </div>
  );
}

function RowCoverSlot({ row, onPick, onClear, locked }) {
  const coverInputRef = useRef(null);
  const [dropOver, setDropOver] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    if (locked) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onPick(file);
  };

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!locked) setDropOver(true); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!locked) setDropOver(true); }}
      onDragLeave={(e) => { e.stopPropagation(); setDropOver(false); }}
      onDrop={onDrop}
      onClick={() => !locked && coverInputRef.current?.click()}
      data-testid={`admin-batch-row-cover-${row.id}`}
      className={`relative h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-lg border transition-colors ${
        dropOver
          ? "border-emerald-300 bg-emerald-300/10"
          : row.cover
            ? "border-sky-400/40"
            : "border-dashed border-white/15 hover:border-emerald-300/40"
      } ${locked ? "cursor-not-allowed opacity-70" : ""}`}
      title={row.cover ? "Replace cover" : "Drop or click to add a cover"}
    >
      {row.coverPreview ? (
        <>
          <img src={row.coverPreview} alt="" className="h-full w-full object-cover" />
          {!locked && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              aria-label="Remove cover"
              data-testid={`admin-batch-row-cover-clear-${row.id}`}
              className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-red-300 hover:bg-red-500/40"
            >
              <X size={9} />
            </button>
          )}
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-zinc-600">
          <ImageIcon size={16} />
        </div>
      )}
      <input
        ref={coverInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onPick(f);
        }}
        data-testid={`admin-batch-row-cover-input-${row.id}`}
      />
    </div>
  );
}
